import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { makeStyles } from 'tss-react/mui';
import AutoCompleteRenderChips, { IAutoCompleteOption } from '../Common/AutoCompleteRenderChips';
import { useSelector } from 'react-redux';
import { selectOrderPriorities, selectSubmissionRequirement } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import {
  DisableOrderPriorityList,
  DisableOrderPriorityListWithoutVoiceAdvantage,
  TooltipOrderPriorityList,
  VirtualInterviewAssociatedList,
  VirtualInterviewRatingsId,
  VoiceAdvantage,
} from '@AMIEWEB/NewOrder/constants';
import {
  getIfElementOfArrayPresentInAnotherArray,
  getNameWithIds,
} from '../helper';
import { virtualRatingListWithVoiceAdvantage } from 'app/constants';
import { useAtoIValuesAlreadyPresent, useVoiceAdvantageOptionDisabled } from '../hooks';

export const VIOrderPriorityAutoComplete = ({
  name,
  options,
  handleChange,
  selectedValues,
  label,
  getOptionDisabled,
}: {
  name: string;
  options: IAutoCompleteOption[];
  handleChange: (data) => void;
  selectedValues: IAutoCompleteOption[];
  label: string;
  getOptionDisabled?: (option: IAutoCompleteOption) => boolean;
}) => {
  return (
    <AutoCompleteRenderChips
      label={label}
      options={options}
      getOptionDisabled={getOptionDisabled}
      id={name}
      name={name}
      selectedValues={selectedValues}
      onValueChange={data => {
        handleChange(data);
      }}
      defaultOptionsToPersist={TooltipOrderPriorityList}
    />
  );
};

const useStyles = makeStyles()(theme => ({
  checkbox: {
    maxWidth: '120px',
    minWidth: '120px',
    '& label': {
      paddingLeft: '0px',
      margin: '0px',
    },
    '& .MuiTypography-root': {
      fontSize: '14px !important',
    },
  },
}));
const VirtualRatingsOrderPriorities = ({ name, checkboxName }: { name: string; checkboxName?: string }) => {
  const { t } = useTranslation();
  const orderPriorities = useSelector(selectOrderPriorities);
  const orderPrioritiesOptions = getNameWithIds(orderPriorities);
  const selectedSubmissionRequirement = useSelector(selectSubmissionRequirement);
  const virtualRatingOptions =
    orderPriorities
      ?.filter(orderPriority => virtualRatingListWithVoiceAdvantage.includes(orderPriority?.name))
      ?.map(item => ({
        ...item,
      })) ?? [];

  const { control, setValue, watch } = useFormContext();
  const { classes } = useStyles();
  const virtualRatingsOrderPriorities = watch(name);
  const selectedOrderPriorities = getNameWithIds(virtualRatingsOrderPriorities);
  const isVoiceAdvantageOptionDisabled = useVoiceAdvantageOptionDisabled(virtualRatingsOrderPriorities);
  const checkIfOptionSelected = virtualRatingsOrderPriorities?.some(a =>
    virtualRatingOptions?.some(c => c?.value === a?.value),
  );
  const isAToIAlreadyPresent = useAtoIValuesAlreadyPresent(selectedOrderPriorities);

  const handleChange = (data: IAutoCompleteOption[]) => {
    const voiceAdvantageValue = {
      name: VoiceAdvantage,
      value: VirtualInterviewRatingsId.VoiceAdvantage,
      hideDeleteIcon: true,
    };
    if (!checkboxName) {
      data = data.map(pri => {
        return { ...pri, name: pri.name.replace(`${pri.value}. `, '') };
      });
    }
    let rating = data;
    const index = data?.findIndex(a => a?.value === VirtualInterviewRatingsId.VoiceAdvantage);
    const isAToIExists = getIfElementOfArrayPresentInAnotherArray(data, 'value', VirtualInterviewAssociatedList);
    if (isAToIExists) {
      if (index === -1) {
        rating.push({ ...voiceAdvantageValue });
      }
      if (index > -1) {
        rating[index] = { ...voiceAdvantageValue };
      }
    } else if (isAToIAlreadyPresent && !isAToIExists) {
      if (rating?.findIndex(a => a?.value === VirtualInterviewRatingsId.VoiceAdvantage) > -1) rating?.splice(index, 1);
    }
    if (checkboxName && !data?.some(a => virtualRatingOptions?.some(c => c?.value === a?.value)))
      setValue(checkboxName, false, { shouldValidate: true });
    setValue(name, rating, { shouldValidate: true });
  };

  return (
    <>
      {checkboxName ? (
        <Grid container xs={12}>
          <Grid item xs={8.75} marginRight={2}>
            <VIOrderPriorityAutoComplete
              name={name}
              options={virtualRatingOptions}
              handleChange={handleChange}
              selectedValues={virtualRatingsOrderPriorities}
              label={`${t('facility.units.unitDetails.additionalInformation.virtualInterviewRating')}`}
              getOptionDisabled={option => {
                if (isVoiceAdvantageOptionDisabled && option?.value === VirtualInterviewRatingsId.VoiceAdvantage) {
                  return true;
                }
                return false;
              }}
            />
          </Grid>
          <Grid item pt={!checkIfOptionSelected ? '4px' : '14px'} className={classes.checkbox}>
            <Controller
              name={checkboxName}
              control={control}
              render={({ field}) => (
                <SimpleCheckbox
                  isChecked={!checkIfOptionSelected ? false : field.value || false}
                  handleChecked={event => {
                    field.onChange(event.target.checked);
                  }}
                  checkboxLabel={t('facility.units.unitDetails.additionalInformation.submissionRequirement')}
                  checkboxName={checkboxName}
                  id={checkboxName}
                  defaultValue={selectedSubmissionRequirement}
                  isdisabled={!checkIfOptionSelected}
                />
              )}
            />
          </Grid>
        </Grid>
      ) : (
        <VIOrderPriorityAutoComplete
          name={name}
          options={orderPrioritiesOptions}
          handleChange={handleChange}
          selectedValues={selectedOrderPriorities}
          label={t('facility.units.unitDetails.additionalInformation.orderPriorities')}
          getOptionDisabled={option => {
            return isAToIAlreadyPresent
              ? DisableOrderPriorityList.includes(option?.value)
              : DisableOrderPriorityListWithoutVoiceAdvantage.includes(option?.value);
          }}
        />
      )}
    </>
  );
};

export default VirtualRatingsOrderPriorities;
